/**
 * EL SOCKET DE SOLO-LECTURA (/api/rt). Un singleton por DOCUMENTO: el servidor solo avisa
 * (invitaciones, cambios de la sesión, señales); el cliente NO manda nada por acá — toda mutación
 * es REST por `api.ts`, que es donde viven `requireRole`, zod, `hitLimit` y la cabecera
 * `x-idle-ms`. Un socket abierto no es una persona (LEY actividad-humana-vs-polling).
 *
 * `TAB_ID` es por documento, a propósito SIN `sessionStorage`: una ventana abierta con
 * `window.open` y una pestaña duplicada HEREDAN el sessionStorage del opener (HTML §12.2.2), y con
 * un tabId persistido las dos creerían ser el mismo viewer. Recargar = tabId nuevo = handoff.
 *
 * Reconexión: backoff 0,5·2ⁿ s hasta 8 s + jitter 0-8 s (peer.ts), y al instante al volver la red o
 * la pestaña. 1006 (corte / SIGKILL antes del handler) se trata igual que 1012 (deploy). Watchdog:
 * 45 s sin `hb` → se cierra y se reconecta (el `ws.ping()` del servidor lo contesta el navegador solo
 * y no lo vemos desde JS).
 *
 * El resync tras reconectar NO vive acá: `rt` solo emite `reconnected` y el ShareProvider decide si
 * tiene una sesión pendiente/viva que justifique un GET (los ~90 de 100 sockets sin sesión no hacen
 * ningún GET al volver).
 *
 * "No habilitado" (spec §4.11): a un usuario real sin habilitación (inspector con
 * `SCREEN_SHARE_INSPECTORS` apagado, correo fuera del piloto) el hub le ACEPTA el upgrade y lo cierra
 * en el acto con el código propio 4403 `share_not_enabled`: acá se apaga del todo (`disabled`) y solo
 * `start()` —que llega con el próximo /me— lo revive. Medido en el e2e: un solo upgrade por pestaña.
 * El 401 HTTP queda para quien NO tiene identidad (cookie muerta, Origin ajeno): desde JS se ve como
 * `close` 1006 sin haber abierto nunca, indistinguible de un corte de red — y un 1006 pre-open también
 * lo produce el `socket.destroy()` del drenado (hub.ts con `wss` null tras `cerrarRealtime`) o un hipo
 * del proxy al cargar. Por eso tras `MAX_PREOPEN_FAILS` cierres seguidos sin un solo `open` NO se apaga
 * (apagarse dejaba la pestaña sin socket hasta cambiar de pestaña, con la tarjeta llegando solo por
 * push): se sigue `wanted` con UN intento por `SLOW_RETRY_MS` (sin bucle corto), y `online` / volver a
 * la pestaña / `start()` prueban al instante. `disabled` queda SOLO para 4401/4403.
 */
import { backoffMs } from './share/peer';
import type { RtMessage } from './share/types';

export const TAB_ID: string = (() => {
  try { return crypto.randomUUID(); }
  catch { return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; }
})();

export type RtStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'disabled';

type MsgType = RtMessage['t'];
type Handler<T extends MsgType> = (m: Extract<RtMessage, { t: T }>) => void;
type StatusHandler = (s: RtStatus, info: { reconnected: boolean; closeCode: number | null }) => void;

const HB_WATCHDOG_MS = 45_000;
const STOP_GRACE_MS = 80; // StrictMode: start→stop→start en el mismo tick no debe cerrar el socket
/** Cierres seguidos SIN haber abierto nunca antes de pasar al reintento LENTO (401 del upgrade: sin identidad; se ve como 1006). */
export const MAX_PREOPEN_FAILS = 4;
/** Reintento lento tras `MAX_PREOPEN_FAILS`: un upgrade por minuto, no un bucle con backoff. */
export const SLOW_RETRY_MS = 60_000;
/** Códigos de cierre propios del servidor para "no habilitado" (hub.ts `CLOSE_SHARE_NOT_ENABLED` = 4403; 4401 reservado). */
const CLOSE_NOT_ENABLED = new Set([4401, 4403]);

const handlers = new Map<MsgType, Set<(m: never) => void>>();
const statusHandlers = new Set<StatusHandler>();

let ws: WebSocket | null = null;
let wanted = false;
let status: RtStatus = 'idle';
let attempt = 0;
let everOpened = false;
let reconnectTimer: number | null = null;
let stopTimer: number | null = null;
let watchdog: number | null = null;
let lastCloseCode: number | null = null;
let preOpenFails = 0;
let listenersInstalled = false;
let lockRelease: (() => void) | null = null;
let hubInstance: string | null = null;

function url(): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  // `ws://` con los dos puntos: sin ellos el navegador lo toma como ruta relativa
  // (`/ws//host/api/rt`) y el socket nunca llega al backend.
  return `${proto}://${location.host}/api/rt?tab=${encodeURIComponent(TAB_ID)}`;
}

function setStatus(s: RtStatus, info: { reconnected: boolean; closeCode: number | null }): void {
  status = s;
  for (const h of statusHandlers) { try { h(s, info); } catch { /* un oyente roto no frena a los demás */ } }
}

function emit(m: RtMessage): void {
  const set = handlers.get(m.t);
  if (!set) return;
  for (const h of set) { try { (h as (x: RtMessage) => void)(m); } catch { /* idem */ } }
}

function armWatchdog(): void {
  if (watchdog != null) clearTimeout(watchdog);
  watchdog = window.setTimeout(() => {
    // 45 s sin latido: el socket está muerto aunque el navegador crea que no.
    watchdog = null;
    if (ws) { try { ws.close(4000, 'hb-timeout'); } catch { /* ya cerrado */ } }
  }, HB_WATCHDOG_MS);
}

function clearTimers(): void {
  if (reconnectTimer != null) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (watchdog != null) { clearTimeout(watchdog); watchdog = null; }
}

function scheduleReconnect(): void {
  if (!wanted || reconnectTimer != null) return;
  const delay = backoffMs(attempt);
  attempt += 1;
  setStatus('reconnecting', { reconnected: false, closeCode: lastCloseCode });
  reconnectTimer = window.setTimeout(() => { reconnectTimer = null; connect(); }, delay);
}

/** Reconexión inmediata (volvió la red / la pestaña): salta el backoff que estuviera corriendo. */
function reconnectNow(): void {
  if (!wanted) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  if (reconnectTimer != null) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  attempt = 0;
  connect();
}

function connect(): void {
  if (!wanted || typeof WebSocket === 'undefined') return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  let sock: WebSocket;
  try { sock = new WebSocket(url()); }
  catch { scheduleReconnect(); return; }
  ws = sock;
  setStatus('connecting', { reconnected: false, closeCode: null });
  sock.onopen = () => {
    if (sock !== ws) return;
    const reconnected = everOpened;
    everOpened = true;
    attempt = 0;
    preOpenFails = 0;
    armWatchdog();
    setStatus('open', { reconnected, closeCode: null });
  };
  sock.onmessage = (ev) => {
    if (sock !== ws) return;
    armWatchdog();
    let m: RtMessage | null = null;
    try { m = JSON.parse(String(ev.data)) as RtMessage; } catch { return; }
    if (!m || typeof m !== 'object' || typeof m.t !== 'string') return;
    if (m.t === 'hello') hubInstance = m.hubInstance;
    emit(m);
  };
  // Un 'error' sin oyente es exactamente lo que no se puede permitir; acá solo se registra y el
  // 'close' que le sigue programa la reconexión.
  sock.onerror = () => { /* el close trae el código */ };
  sock.onclose = (ev) => {
    if (sock !== ws) return;
    ws = null;
    lastCloseCode = ev.code;
    clearTimers();
    // 'dup-tab' (otro socket con MI tabId) y 'too-many' son cierres a propósito del servidor:
    // reconectar pelearía con la otra pestaña. Solo se vuelve si el usuario vuelve a la pestaña.
    const deliberate = ev.code === 1000 && (ev.reason === 'dup-tab' || ev.reason === 'too-many');
    if (!wanted || deliberate) { setStatus(wanted ? 'reconnecting' : 'idle', { reconnected: false, closeCode: ev.code }); return; }
    // "No habilitado" dicho por el servidor (4401/4403): se apaga del todo hasta el próximo start().
    if (CLOSE_NOT_ENABLED.has(ev.code)) { wanted = false; setStatus('disabled', { reconnected: false, closeCode: ev.code }); return; }
    // 401 en el upgrade (rechazo del hub sin identidad: cookie muerta, Origin ajeno), el drenado del deploy
    // o un hipo del proxy se ven desde JS como 1006 sin haber abierto nunca. Tras varios seguidos se baja
    // el ritmo a UN intento por minuto (`wanted` sigue en true): antes se pasaba a `disabled` y la
    // pestaña quedaba sin socket hasta cambiar de pestaña o volver la red.
    if (!everOpened && ev.code === 1006) {
      preOpenFails += 1;
      if (preOpenFails >= MAX_PREOPEN_FAILS) {
        setStatus('reconnecting', { reconnected: false, closeCode: ev.code });
        reconnectTimer = window.setTimeout(() => { reconnectTimer = null; connect(); }, SLOW_RETRY_MS);
        return;
      }
    }
    // 1012 (deploy), 1006 (corte), 4000 (watchdog): mismo camino, backoff + jitter.
    scheduleReconnect();
  };
}

function installListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('online', reconnectNow);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) reconnectNow(); });
}

export const rt = {
  get status(): RtStatus { return status; },
  get hubInstance(): string | null { return hubInstance; },
  get tabId(): string { return TAB_ID; },

  /** Idempotente y StrictMode-safe: si hay un stop pendiente del mismo tick, lo cancela. */
  start(): void {
    wanted = true;
    // Un start() nuevo viene con un /me nuevo: se vuelve a probar desde cero aunque estuviera `disabled`.
    if (status === 'disabled') { preOpenFails = 0; attempt = 0; }
    installListeners();
    if (stopTimer != null) { clearTimeout(stopTimer); stopTimer = null; }
    connect();
  },

  /** Cierra con una gracia mínima (StrictMode desmonta y vuelve a montar en el mismo tick). */
  stop(): void {
    wanted = false;
    if (stopTimer != null) clearTimeout(stopTimer);
    stopTimer = window.setTimeout(() => {
      stopTimer = null;
      if (wanted) return;
      clearTimers();
      const s = ws; ws = null;
      if (s) { try { s.close(1000, 'bye'); } catch { /* ya cerrado */ } }
      attempt = 0;
      preOpenFails = 0;
      rt.holdLock(false);
      setStatus('idle', { reconnected: false, closeCode: null });
    }, STOP_GRACE_MS);
  },

  on<T extends MsgType>(type: T, h: Handler<T>): () => void {
    let set = handlers.get(type);
    if (!set) { set = new Set(); handlers.set(type, set); }
    set.add(h as (m: never) => void);
    return () => { set!.delete(h as (m: never) => void); };
  },

  onStatus(h: StatusHandler): () => void {
    statusHandlers.add(h);
    return () => { statusHandlers.delete(h); };
  },

  /** Forzar una reconexión ya (p. ej. tras un 401 resuelto). */
  reconnectNow,

  /**
   * Web Lock 'ppag-rt' mientras haya una sesión pendiente/aceptada/viva: exención documentada del
   * congelado por Energy Saver de Chrome. Se sostiene SOLO con sesión (F4 decide si siempre).
   */
  holdLock(hold: boolean): void {
    if (hold) {
      if (lockRelease || typeof navigator === 'undefined' || !('locks' in navigator)) return;
      let release: (() => void) | null = null;
      const held = new Promise<void>((resolve) => { release = resolve; });
      lockRelease = release;
      void navigator.locks.request('ppag-rt', { mode: 'shared' }, () => held).catch(() => { lockRelease = null; });
    } else if (lockRelease) {
      lockRelease();
      lockRelease = null;
    }
  },
};
